import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    defaultLessonDuration: { type: Number, default: 60 },
    zeroPickupLabel: { type: String, default: "0 minutes (no pre-test)" },
    lessonTypePickupSync: { type: Boolean, default: false },
    lessonPickupOptionsJson: { type: String, default: "[]" },
    driveTestPickupOptionsJson: { type: String, default: "[]" },
    showDriveTestPretestFields: { type: Boolean, default: true },
  };

  connect() {
    this.handleFrameLoad = this.handleMasterLessonFrameLoad.bind(this);
    document.addEventListener('turbo:frame-load', this.handleFrameLoad);

    this.observeInstructorLocationForm();

    if (this.lessonTypePickupSyncValue) {
      requestAnimationFrame(() => this.syncPickupDurationFromLessonType());
    }

    requestAnimationFrame(() => this.toggleDriveTestPretestAssignmentFields());

    // Auto-close quicklook if data attribute is true
    const autoCloseValue =
      this.element && this.element.dataset
        ? this.element.dataset.schoolLessonQuickLookLessonDetailsAutoCloseValue
        : null;

    const masterLessonIdValue =
      this.element && this.element.dataset
        ? this.element.dataset
            .schoolLessonQuickLookLessonDetailsMasterLessonIdValue
        : null;

    if ((autoCloseValue === "true" || autoCloseValue === true)) {
      if(masterLessonIdValue && masterLessonIdValue !== "0") {
        this.showMasterQuickLook(masterLessonIdValue);
      } else {
        alert("Lesson successfully updated as an observation. Closing quick look.");
        this.closeQuickLook({
          preventDefault: () => {},
        });
      }
    }
  }

  disconnect() {
    document.removeEventListener('turbo:frame-load', this.handleFrameLoad);
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  handleMasterLessonFrameLoad(event) {
    if (event.target.id !== 'master_lesson_view_and_edit') return;
    const typeDropdownWrapper = document.getElementById('type-dropdown-wrapper');
    if (!typeDropdownWrapper) return;
    const hasForm = event.target.querySelector('form');
    if (hasForm) {
      typeDropdownWrapper.classList.remove('u-hidden');
    } else {
      typeDropdownWrapper.classList.add('u-hidden');
    }
  }

  async showMasterQuickLook(masterLessonId) {
    const url = `/schools/master-lesson/quick-look/${masterLessonId}`;
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
    });

    if (response.ok) {
      const quickViewElement = document.querySelector(
        "#school-dashboard-lesson-quick-look"
      );
      quickViewElement.classList.remove("u-hidden");

      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      const template = xmlDoc.querySelector("template");
      const targetElement = document.getElementById("school_lesson_quick_look");

      if (template && targetElement) {
        targetElement.innerHTML = "";
        targetElement.appendChild(template.content.cloneNode(true));
      }
    }
  }

  observeInstructorLocationForm() {
    const instructorLocationContainer = document.getElementById(
      "school_quick_look_add_lesson_instructor_and_location_form"
    );

    if (instructorLocationContainer) {
      this.observer = new MutationObserver(() => {
        this.checkForLocationEdit();
      });

      this.observer.observe(instructorLocationContainer, {
        childList: true,
        subtree: true,
      });
    }
  }

  checkForLocationEdit() {
    const locationEditForm = document.getElementById(
      "quicklook-schedule-location-edit"
    );
    const instructorEditForm = document.getElementById(
      "quicklook-schedule-instructor-edit"
    );
    const saveButtonContainer = document.getElementById(
      "school-lesson-quick-look-lesson-save-button"
    );

    if (saveButtonContainer) {
      const saveButton = saveButtonContainer.querySelector("button");

      if (saveButton) {
        if (locationEditForm || instructorEditForm) {
          saveButton.setAttribute("disabled", "disabled");
        } else {
          saveButton.removeAttribute("disabled");
        }
      }
    }

    this.toggleDriveTestPretestAssignmentFields();
  }

  async editLesson(event) {
    event.preventDefault();

    const lesson_id = 45;
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
  }

  getLessonTypeFromForm() {
    const el = document.getElementById("lesson_type");
    return el ? String(el.value || "").trim() : "";
  }

  pickupDurationSelect() {
    return document.getElementById("new_lesson_specific_duration_in_minutes");
  }

  pickupDurationRow() {
    return document.getElementById("pickup-time-duration-row");
  }

  pickupSectionLabel() {
    return document.getElementById("pickup-time-section-label");
  }

  pickupDurationLabel() {
    return document.getElementById("pickup-time-duration-label");
  }

  selectedTestInstructorId() {
    return document.getElementById("instructor_id")?.value || "";
  }

  selectedTestVehicleId() {
    return document.getElementById("vehicle_id")?.value || "";
  }

  pretestInstructorSelect() {
    return document.getElementById("pretest_instructor_id");
  }

  pretestVehicleSelect() {
    return document.getElementById("pretest_vehicle_id");
  }

  enforcePreviousTestInstructorRule() {
    return document.getElementById("enforce_previous_test_instructor_rule")?.value === "true";
  }

  selectedPupilDriveTestPretestDurationMinutes() {
    const selectedPupil = document.querySelector('input[name="selected-item-pupil"]:checked');
    const value = Number(selectedPupil?.dataset?.driveTestPretestDurationMinutes);
    return Number.isNaN(value) ? 0 : value;
  }

  handlePupilSelection() {
    this.syncPickupDurationFromLessonType();
    this.toggleDriveTestPretestAssignmentFields();

    const pickupDurationSelect = this.pickupDurationSelect();
    if (pickupDurationSelect) {
      pickupDurationSelect.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  setPickupDurationRowHidden(hidden) {
    const row = this.pickupDurationRow();
    if (!row) return;

    if (hidden) {
      row.setAttribute("hidden", true);
      row.style.setProperty("display", "none", "important");
    } else {
      row.removeAttribute("hidden");
      row.style.removeProperty("display");
    }
  }

  syncPickupLabelsForLessonType(type) {
    const pickupSectionLabel = this.pickupSectionLabel();
    const pickupDurationLabel = this.pickupDurationLabel();
    const isScheduledDriveTest = type === "DriveTest";

    if (pickupSectionLabel) {
      pickupSectionLabel.classList.toggle("u-hidden", isScheduledDriveTest);
    }

    if (pickupDurationLabel) {
      pickupDurationLabel.textContent = isScheduledDriveTest
        ? "Pre-Test Duration"
        : "Duration";
    }
  }

  parsedPickupOptions(jsonValue) {
    try {
      const parsed = JSON.parse(jsonValue || "[]");
      return Array.isArray(parsed)
        ? parsed.map((value) => Number(value)).filter((value) => !Number.isNaN(value))
        : [];
    } catch (_error) {
      return [];
    }
  }

  minutesToHourString(minutes) {
    const mins = Number(minutes);
    const hours = Math.floor(mins / 60);
    const remainder = mins % 60;
    if (hours === 0) return `${remainder} minutes`;
    const hourLabel = hours === 1 ? "1 hour" : `${hours} hours`;
    return remainder === 0 ? hourLabel : `${hourLabel} ${remainder} minutes`;
  }

  replacePickupOptions(optionValues, selectedValue = null) {
    const sel = this.pickupDurationSelect();
    if (!sel) return;

    if (sel.tagName !== "SELECT") {
      const nextSelectedValue =
        selectedValue === null || selectedValue === undefined
          ? String(sel.value || "")
          : String(selectedValue);
      sel.value = nextSelectedValue;
      return;
    }

    const nextSelectedValue =
      selectedValue === null || selectedValue === undefined
        ? String(sel.value || "")
        : String(selectedValue);

    sel.innerHTML = "";

    optionValues.forEach((minutes) => {
      const option = document.createElement("option");
      option.value = String(minutes);
      option.textContent =
        minutes === 0
          ? this.zeroPickupLabelValue
          : this.minutesToHourString(minutes);
      sel.appendChild(option);
    });

    if ([...sel.options].some((option) => option.value === nextSelectedValue)) {
      sel.value = nextSelectedValue;
    } else if (sel.options.length) {
      sel.selectedIndex = 0;
    }
  }

  /** Scheduled drive test: optional 0 min pre-test. Open slots never use 0 here (slot length must be > 0). */
  syncPickupDurationFromLessonType() {
    if (!this.lessonTypePickupSyncValue) return;

    const t = this.getLessonTypeFromForm();
    this.syncPickupLabelsForLessonType(t);
    const lessonOptions = this.parsedPickupOptions(this.lessonPickupOptionsJsonValue);
    const driveTestOptions = this.parsedPickupOptions(this.driveTestPickupOptionsJsonValue);
    const pickupDurationSelect = this.pickupDurationSelect();
    const currentValue = pickupDurationSelect?.value;

    if (t === "DriveTest") {
      const hidePretestUi = !this.showDriveTestPretestFieldsValue;

      if (hidePretestUi) {
        this.replacePickupOptions([0], "0");
        this.setPickupDurationRowHidden(true);
        this.toggleDriveTestPretestAssignmentFields();
        return;
      }

      this.setPickupDurationRowHidden(false);
      const selectedPupilDefault = this.selectedPupilDriveTestPretestDurationMinutes();
      const nextValue =
        currentValue == null || currentValue === ""
          ? String(selectedPupilDefault)
          : String(currentValue) === String(this.defaultLessonDurationValue)
          ? String(selectedPupilDefault)
          : currentValue;
      this.replacePickupOptions(driveTestOptions.length ? driveTestOptions : [0], nextValue);
      this.toggleDriveTestPretestAssignmentFields();
      return;
    }

    // Open-slot flows reuse this duration for slot length; server requires duration > 0.
    this.setPickupDurationRowHidden(false);
    this.replacePickupOptions(lessonOptions, currentValue);
    this.toggleDriveTestPretestAssignmentFields();
  }

  toggleDriveTestPretestAssignmentFields() {
    const section = document.getElementById("drive-test-pretest-assignment-fields");
    if (!section) return;

    const selectedLessonType = this.getLessonTypeFromForm();
    const selectedDuration = Number(this.pickupDurationSelect()?.value || 0);
    const shouldShow = selectedLessonType === "DriveTest" && selectedDuration > 0;

    section.classList.toggle("u-hidden", !shouldShow);
    section.querySelectorAll("select, input").forEach((field) => {
      field.disabled = !shouldShow;
    });

    if (shouldShow) {
      this.syncDriveTestPretestAssignmentDefaults();
    }
  }

  syncDriveTestPretestAssignmentDefaults() {
    if (this.enforcePreviousTestInstructorRule()) return;
    if (this.getLessonTypeFromForm() !== "DriveTest") return;
    if (Number(this.pickupDurationSelect()?.value || 0) <= 0) return;

    const pretestInstructorSelect = this.pretestInstructorSelect();
    const pretestVehicleSelect = this.pretestVehicleSelect();
    if (!pretestInstructorSelect || !pretestVehicleSelect) return;

    const testInstructorId = String(this.selectedTestInstructorId() || "");
    const testVehicleId = String(this.selectedTestVehicleId() || "");

    if (!pretestInstructorSelect.value && testInstructorId) {
      pretestInstructorSelect.value = testInstructorId;
    }

    if (!pretestVehicleSelect.value && testVehicleId) {
      pretestVehicleSelect.value = testVehicleId;
    }
  }

  handleTypeSelection(event) {
    const type = event.detail?.dispatchItem?.id;
    const fakeEvent = { preventDefault: () => {} };
    switch (type) {
      case "Lesson":            this.showOriginalForm(); this.hideTestDetails(fakeEvent);        break;
      case "DriveTest":         this.showOriginalForm(); this.unhideTestDetails(fakeEvent);      break;
      case "FlexibleShift":     this.showFlexibleShiftPatternForm(fakeEvent);                    break;
      case "OpenSlotLesson":    this.showOpenSlotFlexibleShiftForm(fakeEvent, { lockedType: "lesson" }); break;
      case "OpenSlotDriveTest": this.showOpenSlotFlexibleShiftForm(fakeEvent, { lockedType: "test" });   break;
      case "Classroom":         this.showOriginalForm(); this.setupScheduleClassroom(fakeEvent); break;
      case "Observation":       this.showObservationForm(); break;
    }
  }

  hideFlexibleShiftForms() {
    const flexibleShiftPatternForm = document.getElementById(
      "schedule-flexible-shift-form"
    );
    const openSlotFlexibleShiftForm = document.getElementById(
      "schedule-open-slot-flexible-shift-form"
    );
    flexibleShiftPatternForm?.classList.add("u-hidden");
    openSlotFlexibleShiftForm?.classList.add("u-hidden");
  }

  showOriginalForm() {
    const lessonFormContent = document.getElementById("lesson-form-content");
    const masterForm = document.getElementById("masterform");
    const typeDropdownWrapper = document.getElementById("type-dropdown-wrapper");
    if (lessonFormContent) lessonFormContent.classList.remove("u-hidden");
    if (masterForm) masterForm.classList.add("u-hidden");
    this.hideFlexibleShiftForms();
    if (typeDropdownWrapper) typeDropdownWrapper.classList.remove("u-hidden");
  }

  showObservationForm() {
    const lessonFormContent = document.getElementById("lesson-form-content");
    const masterForm = document.getElementById("masterform");
    if (lessonFormContent) lessonFormContent.classList.add("u-hidden");
    if (masterForm) masterForm.classList.remove("u-hidden");
    this.hideFlexibleShiftForms();

    this.syncPickupDurationFromLessonType();
  }

  closeQuickLook(event) {
    event.preventDefault();

    const quickViewElement = document.querySelector(
      "#school-dashboard-lesson-quick-look"
    );
    quickViewElement.classList.add("u-hidden");
  }

  configureFlexibleShiftType(formElement, { lockedType = null } = {}) {
    const flexibleShiftForm = formElement;
    if (!flexibleShiftForm) return;

    const lessonRadio = flexibleShiftForm.querySelector("[id$='option-lesson']");
    const testRadio = flexibleShiftForm.querySelector("[id$='option-test']");
    const typeSection = flexibleShiftForm.querySelector("#setflexibleslots-test");
    const lessonRadioWrapper = lessonRadio?.closest(".Input--radio");
    const testRadioWrapper = testRadio?.closest(".Input--radio");

    if (!lessonRadio || !testRadio) return;

    const lessonLocked = lockedType === "lesson";
    const testLocked = lockedType === "test";

    lessonRadio.disabled = testLocked;
    testRadio.disabled = lessonLocked;
    lessonRadioWrapper?.classList.toggle("u-hidden", testLocked);
    testRadioWrapper?.classList.toggle("u-hidden", lessonLocked);
    typeSection?.classList.toggle("u-hidden", lessonLocked || testLocked);

    if (lessonLocked) {
      lessonRadio.checked = true;
      testRadio.checked = false;
      lessonRadio.dispatchEvent(new Event("change", { bubbles: true }));
    } else if (testLocked) {
      lessonRadio.checked = false;
      testRadio.checked = true;
      testRadio.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  showFlexibleShiftPatternForm(event) {
    event.preventDefault();
    const lessonFormContent = document.getElementById("lesson-form-content");
    const masterForm = document.getElementById("masterform");
    const flexibleShiftPatternForm = document.getElementById(
      "schedule-flexible-shift-form"
    );
    if (lessonFormContent) lessonFormContent.classList.add("u-hidden");
    if (masterForm) masterForm.classList.add("u-hidden");
    this.hideFlexibleShiftForms();
    flexibleShiftPatternForm?.classList.remove("u-hidden");
  }

  showOpenSlotFlexibleShiftForm(event, { lockedType = null } = {}) {
    event.preventDefault();
    const lessonFormContent = document.getElementById("lesson-form-content");
    const masterForm = document.getElementById("masterform");
    const openSlotFlexibleShiftForm = document.getElementById(
      "schedule-open-slot-flexible-shift-form"
    );
    if (lessonFormContent) lessonFormContent.classList.add("u-hidden");
    if (masterForm) masterForm.classList.add("u-hidden");
    this.hideFlexibleShiftForms();
    this.configureFlexibleShiftType(openSlotFlexibleShiftForm, { lockedType });
    openSlotFlexibleShiftForm?.classList.remove("u-hidden");
  }

  hideTestDetails(event) {
    event.preventDefault();

    const formElement = document.getElementById("test_details_form");
    if (formElement) formElement.setAttribute("hidden", true);

    const formElementStudent = document.getElementById(
      "quick-look-student-book-dropdown"
    );
    formElementStudent.removeAttribute("hidden");

    const formElementSlot = document.getElementById("add-open-slots-form");
    formElementSlot.setAttribute("hidden", true);

    const formElementPickupTime = document.getElementById(
      "add-lesson-pickup-time-form"
    );
    formElementPickupTime.removeAttribute("hidden");

    const formElementSaveButton = document.getElementById(
      "school-lesson-quick-look-lesson-save-button"
    );
    formElementSaveButton.removeAttribute("hidden");

    const formElementInstructor = document.getElementById(
      "school_quick_look_add_lesson_instructor_and_location"
    );
    formElementInstructor.removeAttribute("hidden");

    const formElementScheduleClassroom = document.querySelector(
      "#schedule-classroom-program-form"
    );
    formElementScheduleClassroom.classList.add("u-hidden");

    const pickupDurationSelect = document.getElementById(
      "new_lesson_specific_duration_in_minutes"
    );
    if (
      pickupDurationSelect &&
      pickupDurationSelect.value === "0"
    ) {
      pickupDurationSelect.value = String(this.defaultLessonDurationValue);
    }

    this.syncPickupDurationFromLessonType();
  }

  unhideTestDetails(event) {
    event.preventDefault();

    this.syncPickupDurationFromLessonType();

    const formElement = document.getElementById("test_details_form");
    if (formElement) formElement.removeAttribute("hidden");

    const formElementStudent = document.getElementById(
      "quick-look-student-book-dropdown"
    );
    formElementStudent.removeAttribute("hidden");

    const formElementPickupTime = document.getElementById(
      "add-lesson-pickup-time-form"
    );
    formElementPickupTime.removeAttribute("hidden");

    const formElementSaveButton = document.getElementById(
      "school-lesson-quick-look-lesson-save-button"
    );
    formElementSaveButton.removeAttribute("hidden");

    const formElementSlot = document.getElementById("add-open-slots-form");
    formElementSlot.setAttribute("hidden", false);

    const formElementInstructor = document.getElementById(
      "school_quick_look_add_lesson_instructor_and_location"
    );
    formElementInstructor.removeAttribute("hidden");

    const formElementScheduleClassroom = document.querySelector(
      "#schedule-classroom-program-form"
    );
    formElementScheduleClassroom.classList.add("u-hidden");

    const pickupDurationSelect = this.pickupDurationSelect();
    if (
      pickupDurationSelect &&
      String(pickupDurationSelect.value) ===
        String(this.defaultLessonDurationValue)
    ) {
      pickupDurationSelect.value = "0";
    }

    this.syncPickupDurationFromLessonType();
  }

  setupSlotDetails(event) {
    event.preventDefault();

    const formElement = document.getElementById(
      "quick-look-student-book-dropdown"
    );
    formElement.setAttribute("hidden", true);

    const formElementTest = document.getElementById("test_details_form");
    if (formElementTest) formElementTest.setAttribute("hidden", true);

    const formElementSlot = document.getElementById("add-open-slots-form");
    formElementSlot.removeAttribute("hidden");

    const formElementPickupTime = document.getElementById(
      "add-lesson-pickup-time-form"
    );
    formElementPickupTime.removeAttribute("hidden");

    const formElementSaveButton = document.getElementById(
      "school-lesson-quick-look-lesson-save-button"
    );
    formElementSaveButton.setAttribute("hidden", true);

    const formElementInstructor = document.getElementById(
      "school_quick_look_add_lesson_instructor_and_location"
    );
    formElementInstructor.setAttribute("hidden", true);

    const formElementScheduleClassroom = document.querySelector(
      "#schedule-classroom-program-form"
    );
    formElementScheduleClassroom.classList.add("u-hidden");

    this.syncPickupDurationFromLessonType();
  }

  togglePackageList(event) {
    event.preventDefault();

    const toggle = event.currentTarget;
    const packageList = event.currentTarget.closest(".QuicklookPackageList");
    if (!packageList) return;

    const panel = packageList.querySelector(".QuicklookPackageList__panel");
    const label = packageList.querySelector("[data-quicklook-package-list-label]");
    if (!panel || !toggle || !label) return;

    const isOpen = toggle.getAttribute("aria-expanded") === "true";

    if (isOpen) {
      this.closePackageList({ packageList, panel, toggle, label });
    } else {
      this.openPackageList({ packageList, panel, toggle, label });
    }
  }

  openPackageList({ panel, toggle, label }) {
    toggle.setAttribute("aria-expanded", "true");
    panel.setAttribute("aria-hidden", "false");
    label.textContent = "hide packages";
    panel.style.maxHeight = `${panel.scrollHeight}px`;
    panel.classList.add("is-open");
  }

  closePackageList({ packageList, panel, toggle, label }) {
    toggle.setAttribute("aria-expanded", "false");
    panel.setAttribute("aria-hidden", "true");
    label.textContent = packageList.dataset.quicklookPackageListClosedLabel || "view more";
    panel.style.maxHeight = "0px";
    panel.classList.remove("is-open");
  }

  setupScheduleClassroom(event) {
    event.preventDefault();

    // const formElement = document.getElementById('quick-look-student-book-dropdown');
    // formElement.setAttribute('hidden', true);

    const formElementTest = document.getElementById("test_details_form");
    if (formElementTest) formElementTest.setAttribute("hidden", true);

    const formElementSlot = document.getElementById("add-open-slots-form");
    formElementSlot.setAttribute("hidden", true);

    const formElementInstructor = document.getElementById(
      "school_quick_look_add_lesson_instructor_and_location"
    );
    formElementInstructor.setAttribute("hidden", true);

    const formElementPickupTime = document.getElementById(
      "add-lesson-pickup-time-form"
    );
    formElementPickupTime.setAttribute("hidden", true);

    const formElementSaveButton = document.getElementById(
      "school-lesson-quick-look-lesson-save-button"
    );
    formElementSaveButton.setAttribute("hidden", true);

    const formElementStudent = document.getElementById(
      "quick-look-student-book-dropdown"
    );
    formElementStudent.removeAttribute("hidden");

    // show schedule-classroom-program-form
    const formElementScheduleClassroom = document.querySelector(
      "#schedule-classroom-program-form"
    );
    formElementScheduleClassroom.classList.remove("u-hidden");

    // If a student is already selected, load the classroom programs for them
    // now — without this the admin has to re-tap the student to populate the frame.
    this.reloadClassroomFrameForSelectedPupil();

    this.syncPickupDurationFromLessonType();
  }

  reloadClassroomFrameForSelectedPupil() {
    const selectedPupil = document.querySelector(
      'input[name="selected-item-pupil"]:checked'
    );
    const pupilId = selectedPupil?.value;
    if (!pupilId) return;

    const classroomSection = document.getElementById(
      "schedule-classroom-program-form"
    );
    const classroomFrame = classroomSection?.querySelector("turbo-frame");
    if (!classroomFrame) return;

    // Skip if the frame is already loaded for this pupil.
    const currentSrc = classroomFrame.getAttribute("src") || "";
    const alreadyLoadedForPupil =
      currentSrc.includes(`picked_pupil=${pupilId}`) &&
      classroomFrame.querySelector("#driving-school-programs");
    if (alreadyLoadedForPupil) return;

    const frameUrl = new URL(
      "/schools/classroom/program/quick-look/schedule",
      window.location.origin
    );
    frameUrl.searchParams.set("picked_pupil", pupilId);
    classroomFrame.innerHTML =
      '<div class="u-textCenter p2"><span class="icon icon-spinner2 u-animateSpin u-textGreenMid u-inlineBlock"></span> Loading</div>';
    classroomFrame.setAttribute("src", frameUrl.toString());
  }
}
